import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { ReactComponent as EditIcon } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import Icon from "../../components/common/Icon";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import DeleteModal from "../../components/modals/DeleteModal";
import EditAudienceProfileModal from "./audienceProfiles/EditAudienceProfileModal";
import { getAudienceProfiles, deleteAudienceProfile, AudienceProfile } from "../../api/audienceProfile";
import { useNotification } from "../../context/NotificationContext";
import styles from "./audienceProfiles/AudienceProfilesList.module.css";

const AudienceProfilesList: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const [audienceProfiles, setAudienceProfiles] = useState<AudienceProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState<AudienceProfile | null>(null);

    const fetchAudienceProfiles = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getAudienceProfiles();
            setAudienceProfiles(data);
        } catch (error: any) {
            console.error("Error fetching audience profiles:", error);
            showNotification({
                message: t("audienceProfiles.fetchError") || "Failed to fetch audience profiles",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [showNotification, t]);

    useEffect(() => {
        fetchAudienceProfiles();
    }, [fetchAudienceProfiles]);

    const handleEdit = (profile: AudienceProfile) => {
        setSelectedProfile(profile);
        setIsEditModalOpen(true);
    };

    const handleDeleteClick = (profile: AudienceProfile) => {
        setSelectedProfile(profile);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedProfile) return;

        try {
            await deleteAudienceProfile(selectedProfile.id);
            showNotification({
                message: t("audienceProfiles.deletedSuccessfully") || "Audience profile deleted successfully",
                type: "success",
            });
            await fetchAudienceProfiles();
        } catch (error: any) {
            console.error("Error deleting audience profile:", error);
            showNotification({
                message: t("audienceProfiles.deleteError") || "Failed to delete audience profile",
                type: "error",
            });
        } finally {
            setIsDeleteModalOpen(false);
            setSelectedProfile(null);
        }
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setSelectedProfile(null);
        fetchAudienceProfiles();
    };

    const handleCreateModalClose = (created?: boolean) => {
        setIsCreateModalOpen(false);
        if (created) {
            fetchAudienceProfiles();
        }
    };

    const canCreate = audienceProfiles.length < 10;
    const createTooltip = canCreate
        ? t("audienceProfiles.createNew") || "Créer une nouvelle audience"
        : t("audienceProfiles.maxReached") || "Maximum of 10 audience profiles reached";

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHead}>
                <h1 className={styles.title}>{t("audienceProfiles.listTitle") || "Audiences"}</h1>
                <p className={styles.subtitle}>
                    {t("audienceProfiles.listSubtitle") ||
                        "Décrivez les profils de visiteurs pour lesquels vos contenus sont rédigés. Ces audiences aident l'IA à adapter le ton, le niveau et les exemples."}
                </p>
            </div>

            <div className={styles.bar}>
                <h2 className={styles.barTitle}>
                    {t("audienceProfiles.yourAudiences") || "Vos audiences"}
                    <span className={styles.count}>{audienceProfiles.length}</span>
                </h2>
                <Tooltip title={createTooltip} arrow>
                    <span className={styles.newBtnWrap}>
                        <button
                            type="button"
                            className={styles.newBtn}
                            onClick={() => setIsCreateModalOpen(true)}
                            disabled={!canCreate}
                        >
                            <Icon name="ic-plus" />
                            {t("audienceProfiles.newAudience") || "Nouvelle audience"}
                        </button>
                    </span>
                </Tooltip>
            </div>

            {audienceProfiles.length === 0 ? (
                <div className={styles.empty}>
                    <p className={styles.emptyMessage}>
                        {t("audienceProfiles.noProfiles") || "No audience profiles yet"}
                    </p>
                    <button
                        type="button"
                        className={styles.newBtn}
                        style={{ marginLeft: 0 }}
                        onClick={() => setIsCreateModalOpen(true)}
                        disabled={!canCreate}
                    >
                        <Icon name="ic-plus" />
                        {t("audienceProfiles.createFirst") || "Créer votre première audience"}
                    </button>
                </div>
            ) : (
                <div className={styles.list}>
                    {audienceProfiles.map((profile) => (
                        <article key={profile.id} className={styles.card}>
                            <div className={styles.cardTop}>
                                <h3 className={styles.cardName}>{profile.name}</h3>
                                <div className={styles.cardActs}>
                                    <Tooltip title={t("edit")} arrow>
                                        <button
                                            type="button"
                                            className={styles.iconBtn}
                                            onClick={() => handleEdit(profile)}
                                            aria-label={t("edit")}
                                        >
                                            <EditIcon />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={t("delete")} arrow>
                                        <button
                                            type="button"
                                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                            onClick={() => handleDeleteClick(profile)}
                                            aria-label={t("delete")}
                                        >
                                            <DeleteIcon />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                            {profile.description && <p className={styles.cardDesc}>{profile.description}</p>}
                            <div className={styles.cardFoot}>
                                <span>
                                    <Icon name="ic-clock" />
                                    {t("audienceProfiles.createdAt") || "Créé"}{" "}
                                    {new Date(profile.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedProfile(null);
                }}
                onConfirm={handleConfirmDelete}
                title={t("audienceProfiles.deleteTitle") || "Delete Audience Profile"}
                message={
                    selectedProfile
                        ? t("audienceProfiles.deleteConfirmation", { name: selectedProfile.name }) ||
                          `Are you sure you want to delete "${selectedProfile.name}"? This action cannot be undone.`
                        : t("audienceProfiles.confirmDelete") ||
                          "Are you sure you want to delete this audience profile?"
                }
            />

            {selectedProfile && (
                <EditAudienceProfileModal
                    isOpen={isEditModalOpen}
                    onClose={handleEditModalClose}
                    audienceProfile={selectedProfile}
                    browserLanguage={i18n.language}
                />
            )}

            <EditAudienceProfileModal
                isOpen={isCreateModalOpen}
                onClose={handleCreateModalClose}
                audienceProfile={null}
                browserLanguage={i18n.language}
            />
        </div>
    );
};

export default AudienceProfilesList;
